import { useTranslation } from 'react-i18next';
import { Download, FileText, Music2 } from 'lucide-react';
import { Button, EmptyState } from '@ntconsult/aluma-react';
import { inferFileKind, type FileKind } from '@common/utils/fileKind';
import { fileService } from '../services/fileService';
import { EmbeddedVideoPlayer } from './EmbeddedVideoPlayer';
import type { useVideoPlayer } from '../hooks/useVideoPlayer';
import type { File } from '../types';

type Player = ReturnType<typeof useVideoPlayer>;

interface FileViewerPanelProps {
  file: File;
  player: Player;
  onDownload: () => void;
}

export function FileViewerPanel({
  file,
  player,
  onDownload,
}: FileViewerPanelProps) {
  const kind = inferFileKind(file.mimeType, file.name);

  if (kind === 'video') {
    return <EmbeddedVideoPlayer file={file} player={player} />;
  }

  return <NonVideoPreview file={file} kind={kind} onDownload={onDownload} />;
}

function NonVideoPreview({
  file,
  kind,
  onDownload,
}: {
  file: File;
  kind: FileKind;
  onDownload: () => void;
}) {
  const { t } = useTranslation('files');
  const streamUrl = fileService.getStreamProxyUrl(file.id);

  if (kind === 'audio') {
    return (
      <div className="border border-border rounded-lg p-6 flex items-center gap-4 bg-surface">
        <Music2
          className="w-8 h-8 text-foreground-muted shrink-0"
          aria-hidden="true"
        />
        <audio src={streamUrl} controls className="w-full" />
      </div>
    );
  }

  if (kind === 'image') {
    return (
      <img
        src={streamUrl}
        alt={file.name}
        className="max-w-full max-h-[70vh] mx-auto rounded-lg"
      />
    );
  }

  if (kind === 'pdf') {
    return (
      <iframe
        src={streamUrl}
        title={file.name}
        className="w-full h-[75vh] border border-border rounded-lg bg-white"
      />
    );
  }

  return (
    <EmptyState
      decorator={
        <FileText
          className="w-8 h-8 text-foreground-muted"
          aria-hidden="true"
        />
      }
      decoratorPlacement="top"
      align="center"
      card
      title={t('preview.unavailable')}
      description={t('preview.unavailableHint')}
      action={
        <Button
          onClick={onDownload}
          variant="primary"
          size="sm"
          leftIcon={<Download className="w-4 h-4" aria-hidden="true" />}
        >
          {t('preview.download')}
        </Button>
      }
    />
  );
}
